import {
  captureFormContract,
  captureFormDefinition,
  captureFormValues,
} from './workflow-capture-form';
import type { WorkflowClientAction, WorkflowClientCommandModule } from './workflow-command-registry';
import { captureLookupRevisions, resolveCaptureLookupFields, workflowFormLookupBindingKey } from './workflow-form-lookup';
import type { GeneratedFormDefinition, GeneratedFormValues } from '@template-ui/helpers/generated-form.helpers';

const submitForm = {
  key: {
    provider: 'workflow',
    kind: 'command',
    code: 'workflow.form.submit',
    version: 1,
  },
  form: captureFormContract,
  endpointSuffix: '/forms/submit',
  submitLabel: 'Submit form and continue',
  submissionHint: 'Saves these answers and completes this visit together.',
  confirmation: 'Form submitted.',
  bindContext: (context) => ({
    occurrenceId: context.occurrenceId,
    expectedETag: context.stepETag,
  }),
  formForAction: (action) => captureFormDefinition(action.form?.captureForm),
  draftContextKey: (action: WorkflowClientAction) => {
    const form = action.form?.captureForm;
    return form?.version === 3 ? JSON.stringify([form.id, form.fields.filter(field => field.lookup != null)
      .map(field => [field.id, workflowFormLookupBindingKey(field.lookup!)])]) : null;
  },
  serializeInput: (form: GeneratedFormDefinition, values: GeneratedFormValues, action?: WorkflowClientAction) => {
    const capture = action?.form?.captureForm, answers = captureFormValues(form, values);
    if (capture?.version !== 3) return { valuesJson: JSON.stringify(answers) };
    const editableAnswers = Object.fromEntries(Object.entries(answers)
      .filter(([name]) => form.fields.some(field => field.name === name && !field.readOnly)));
    const resolved = resolveCaptureLookupFields(form, capture, action?.form?.lookupFields);
    const revisions = resolved ? captureLookupRevisions(form, capture, resolved.revisions, values) : null;
    return revisions === null ? null : { valuesJson: JSON.stringify({ version: 1, values: editableAnswers, lookupRevisions: revisions }) };
  },
} satisfies WorkflowClientCommandModule['commands'][number];

export const workflowCaptureCommandModule: WorkflowClientCommandModule = {
  code: 'workflow.forms',
  version: 1,
  commands: [
    submitForm,
    {
      ...submitForm,
      key: { ...submitForm.key, code: 'workflow.form.save' },
      endpointSuffix: '/forms/save',
      submitLabel: 'Save answers',
      submissionHint:
        'Saves these answers for this visit. Complete the step when all requirements are met.',
      confirmation: 'Answers saved.',
    },
  ],
};
